import type {ValuationData} from "@reva/shared/valuation-data";
import equal from "fast-deep-equal";

import {isCurrentEnvironmentInDevelopMode} from "../environment";

const IGNORED_KEYS = ["durationMillis", "metadata"];

/**
 * Vergleicht Persistenz beim Starten der Finalisierung mit der gerade geladenen Persistenz aus der API.
 * @param persistenceFromApi Die aktuell von der API geladene Persistenz
 * @param persistenceSnapshot Snapshot der Persistenz zum Zeitpunkt des Dialog-Öffnens
 * @returns true wenn Änderungen gefunden wurden, sonst false
 */
export function checkForPersistenceChanges(
    persistenceFromApi: ValuationData,
    persistenceSnapshot: ValuationData,
): boolean {
    const lastUpdateDateFromApi = persistenceFromApi.metadata?.lastUpdateDate;
    const lastUpdateDateFromStore = persistenceSnapshot.metadata?.lastUpdateDate;
    const isStoreUpToDate =
        lastUpdateDateFromApi && lastUpdateDateFromStore && lastUpdateDateFromApi <= lastUpdateDateFromStore;

    const devMode = isCurrentEnvironmentInDevelopMode();
    const LOG_PREFIX = "Checked persistence changes:";

    if (persistenceFromApi.addressQuery !== persistenceSnapshot.addressQuery) {
        if (devMode) console.info(LOG_PREFIX, "Address query changed. Changes detected.");
        return true;
    }
    // Wenn das Aktualisierungsdatum aus dem Store größer oder gleich der Aktualisierungsdatum von der API ist kann es keine Änderungen geben
    if (isStoreUpToDate) {
        if (devMode) console.info(LOG_PREFIX, "Store is up to date with API data. No changes detected.");
        return false;
    }

    const apiKeys = Object.keys(persistenceFromApi);
    const storeKeys = Object.keys(persistenceSnapshot);
    if (!equal(storeKeys, apiKeys)) {
        if (devMode) console.info(LOG_PREFIX, "Store keys do not match API keys. Changes detected.");
        return true;
    }

    // Sonst prüfen wir alle relevanten Felder auf Gleichheit
    let hasChanged = false;
    const keysToCheck = storeKeys.filter((key) => !IGNORED_KEYS.includes(key));
    for (const key of keysToCheck) {
        if (
            !equal(
                (persistenceFromApi as unknown as Record<string, unknown>)[key],
                (persistenceSnapshot as unknown as Record<string, unknown>)[key],
            )
        ) {
            hasChanged = true;
            break;
        }
    }
    if (devMode) console.info(LOG_PREFIX, `Store has changed: ${hasChanged}`);
    return hasChanged;
}

export function collectDifferences(
    objA: Record<string, unknown>,
    objB: Record<string, unknown>,
    path = "",
    differences: Record<string, [unknown, unknown]> = {},
): Record<string, [unknown, unknown]> {
    const allKeys = new Set([...Object.keys(objA || {}), ...Object.keys(objB || {})]);

    for (const key of allKeys) {
        if (IGNORED_KEYS.includes(key)) continue;
        const currentPath = path ? `${path}.${key}` : key;
        const valueA = objA?.[key];
        const valueB = objB?.[key];

        // Wenn Werte gleich sind, weitermachen
        if (equal(valueA, valueB)) {
            continue;
        }

        // Wenn beide Werte Objekte (aber nicht null/undefined) sind, rekursiv prüfen
        if (
            typeof valueA === "object" &&
            valueA !== null &&
            typeof valueB === "object" &&
            valueB !== null &&
            !Array.isArray(valueA) &&
            !Array.isArray(valueB)
        ) {
            collectDifferences(
                valueA as Record<string, unknown>,
                valueB as Record<string, unknown>,
                currentPath,
                differences,
            );
        } else {
            // Unterschied speichern
            differences[key] = [valueA, valueB];
        }
    }
    return differences;
}
